import {
  Component, Input, HostListener, OnInit, OnDestroy,
  inject, ElementRef, ViewChild, AfterViewInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { StickyNote, StickyNotesService } from '@app/services/sticky-notes.service';

const COLORS = [
  '#FFEE58', '#66BB6A', '#42A5F5', '#EC407A', '#FFA726',
  '#AB47BC', '#78909C', '#EF5350', '#26C6DA', '#9CCC65',
  '#FF7043', '#5C6BC0', '#8D6E63',
];

@Component({
  selector: 'app-sticky-note-window',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule],
  templateUrl: './sticky-note-window.component.html',
  styleUrls: ['./sticky-note-window.component.scss'],
})
export class StickyNoteWindowComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() note!: StickyNote;
  @ViewChild('winRef') winRef!: ElementRef<HTMLDivElement>;

  private service = inject(StickyNotesService);
  private destroy$ = new Subject<void>();
  private saveSubject$ = new Subject<void>();

  localTitle     = '';
  localContent   = '';
  savedIndicator = false;
  showColorPicker = false;
  colors = COLORS;

  get textColor(): string {
    const hex = this.note.color?.replace('#', '') ?? '000000';
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.55 ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.9)';
  }

  private posX = 0; private posY = 0;
  private w = 260;  private h = 220;

  private dragging  = false;
  private ds        = { mx: 0, my: 0, ex: 0, ey: 0 };
  private dragMoved = false;

  private resizing = false;
  private rs       = { mx: 0, my: 0, w: 0, h: 0 };

  ngOnInit(): void {
    this.localTitle   = this.note.title   ?? '';
    this.localContent = this.note.content ?? '';
    this.posX = this.note.pos_x;
    this.posY = this.note.pos_y;
    this.w    = this.note.width;
    this.h    = this.note.height;

    this.saveSubject$.pipe(
      debounceTime(500),
      takeUntil(this.destroy$),
    ).subscribe(() => this.save());
  }

  ngAfterViewInit(): void {
    if (!this.posX && !this.posY) {
      this.posX = 120;
      this.posY = 120;
    }
    this.applyGeometry();
    this.winRef.nativeElement.style.zIndex = String(
      this.note.z_index > 0 ? this.note.z_index : 9100
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyGeometry(): void {
    const el = this.winRef.nativeElement;
    el.style.left   = this.posX + 'px';
    el.style.top    = this.posY + 'px';
    el.style.width  = this.w   + 'px';
    el.style.height = this.h   + 'px';
  }

  onContentChange(): void {
    this.saveSubject$.next();
  }

  private save(): void {
    this.service.update(this.note.id, {
      title: this.localTitle,
      content: this.localContent,
    }).subscribe({
      next: () => {
        this.savedIndicator = true;
        setTimeout(() => this.savedIndicator = false, 2000);
      },
      error: (e) => console.error('[StickyNotes] auto-save failed', e),
    });
  }

  close(): void {
    this.service.setOpen(this.note.id, false);
  }

  deleteNote(): void {
    this.service.delete(this.note.id).subscribe();
  }

  pickColor(color: string): void {
    this.showColorPicker = false;
    if (color === this.note.color) return;
    this.service.update(this.note.id, { color }).subscribe();
  }

  onWindowMousedown(): void {
    const newZ = this.service.bringToFront(this.note.id);
    this.winRef.nativeElement.style.zIndex = String(newZ);
  }

  startDrag(e: MouseEvent): void {
    e.preventDefault();
    this.dragging  = true;
    this.dragMoved = false;
    const rect = this.winRef.nativeElement.getBoundingClientRect();
    this.ds = { mx: e.clientX, my: e.clientY, ex: rect.left, ey: rect.top };
  }

  @HostListener('document:mousemove', ['$event'])
  onMove(e: MouseEvent): void {
    if (this.dragging) {
      const dx = e.clientX - this.ds.mx;
      const dy = e.clientY - this.ds.my;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) this.dragMoved = true;
      if (!this.dragMoved) return;
      this.posX = Math.max(0, Math.min(window.innerWidth - 50, this.ds.ex + dx));
      this.posY = Math.max(0, Math.min(window.innerHeight - 40, this.ds.ey + dy));
      this.winRef.nativeElement.style.left = this.posX + 'px';
      this.winRef.nativeElement.style.top  = this.posY + 'px';
    }
    if (this.resizing) {
      this.w = Math.max(200, this.rs.w + (e.clientX - this.rs.mx));
      this.h = Math.max(200, this.rs.h + (e.clientY - this.rs.my));
      this.winRef.nativeElement.style.width  = this.w + 'px';
      this.winRef.nativeElement.style.height = this.h + 'px';
    }
  }

  @HostListener('document:mouseup')
  onUp(): void {
    if (this.dragging) {
      this.dragging = false;
      if (this.dragMoved) {
        this.service.saveGeometry(this.note.id, { pos_x: this.posX, pos_y: this.posY });
      }
    }
    if (this.resizing) {
      this.resizing = false;
      this.service.saveGeometry(this.note.id, { width: this.w, height: this.h });
    }
  }

  startResize(e: MouseEvent): void {
    e.preventDefault(); e.stopPropagation();
    this.resizing = true;
    this.rs = { mx: e.clientX, my: e.clientY, w: this.w, h: this.h };
  }
}
